import { useTranslation } from 'react-i18next';
import { BadgeDollarSign, FlaskConical, HandCoins, Percent, Receipt } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { KpiCard, SectionCard, formatNumber } from '../../../components/design-system/admin';
import { DistributionBar } from '../../../components/design-system/analytics';
import type { UnitEconomicsResponse } from '../../../common/models/unit-economics.model';
import { money, moneyShort } from '../costs/cost-format';
import { formatMultiple, platformCostParts } from './ue-format';

const pct = (v: number | null | undefined) => (v == null ? '—' : `${formatNumber(v, 1)}%`);

/** The month in five numbers: MRR, cost to serve, gross margin, trials, sales influenced. */
export function EconomicsKpis({ data }: { data: UnitEconomicsResponse | null }) {
  const { t } = useTranslation();
  const tt = data?.totals;
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5" data-testid="ue-kpis">
      <KpiCard
        label={t('UNIT_ECONOMICS.KPI.MRR')}
        icon={<BadgeDollarSign />}
        loading={!data}
        value={moneyShort(tt?.mrrUsd)}
        hint={tt ? t('UNIT_ECONOMICS.KPI.MRR_HINT', { count: tt.payingCustomers, arr: moneyShort(tt.arrUsd) }) : null}
        testId="ue-kpi-mrr"
      />
      <KpiCard
        label={t('UNIT_ECONOMICS.KPI.COST')}
        icon={<Receipt />}
        loading={!data}
        value={moneyShort(tt?.totalCostUsd)}
        hint={tt ? t('UNIT_ECONOMICS.KPI.COST_HINT', { direct: moneyShort(tt.directCostUsd), shared: moneyShort(tt.allocatedCostUsd), unattributed: moneyShort(tt.unattributedCostUsd) }) : null}
        testId="ue-kpi-cost"
      />
      <KpiCard
        label={t('UNIT_ECONOMICS.KPI.MARGIN')}
        icon={<Percent />}
        loading={!data}
        value={<span className={tt && tt.grossMarginUsd < 0 ? 'text-destructive' : undefined}>{pct(tt?.grossMarginPct)}</span>}
        hint={
          tt
            ? tt.revenueUsd > 0
              ? t('UNIT_ECONOMICS.KPI.MARGIN_HINT', { amount: moneyShort(tt.grossMarginUsd), revenue: moneyShort(tt.revenueUsd), paying: pct(tt.payingMarginPct) })
              : t('UNIT_ECONOMICS.KPI.MARGIN_NONE')
            : null
        }
        testId="ue-kpi-margin"
      />
      <KpiCard
        label={t('UNIT_ECONOMICS.KPI.TRIALS')}
        icon={<FlaskConical />}
        loading={!data}
        value={tt ? formatNumber(tt.trials.count) : '—'}
        hint={tt ? t('UNIT_ECONOMICS.KPI.TRIALS_HINT', { cost: money(tt.trials.costUsd), demos: money(tt.demos.costUsd) }) : null}
        testId="ue-kpi-trials"
      />
      <KpiCard
        label={t('UNIT_ECONOMICS.KPI.VALUE')}
        icon={<HandCoins />}
        loading={!data}
        value={moneyShort(tt?.influencedUsd)}
        hint={tt ? (tt.roi != null ? t('UNIT_ECONOMICS.KPI.VALUE_HINT', { roi: formatMultiple(tt.roi) }) : t('UNIT_ECONOMICS.KPI.VALUE_NONE')) : null}
        testId="ue-kpi-value"
      />
    </div>
  );
}

/** Where the month's cost went, and how each shared bill was split. */
export function CostSplitCard({ data, className }: { data: UnitEconomicsResponse | null; className?: string }) {
  const { t } = useTranslation();
  if (!data) return <Skeleton className={`h-48 rounded-xl ${className || ''}`} />;
  const parts = platformCostParts(data);
  const un = data.totals.unattributed;
  return (
    <SectionCard title={t('UNIT_ECONOMICS.SPLIT.TITLE')} description={t('UNIT_ECONOMICS.SPLIT.DESCRIPTION')} className={className} testId="ue-split">
      <DistributionBar
        segments={parts.map((p) => ({ key: p.key, label: t(`UNIT_ECONOMICS.COST_PARTS.${p.key}`), value: p.value }))}
        format={(v) => money(v)}
        columns={3}
      />
      <ul className="mt-4 flex flex-col gap-1 border-t pt-3 text-xs text-muted-foreground">
        {data.pools.map((p) => {
          const name = t(`UNIT_ECONOMICS.COST_PARTS.${p.key === 'gpu' ? 'gpu' : 'hosting'}`);
          if (p.monthUsd == null) return <li key={p.key}>{t('UNIT_ECONOMICS.SPLIT.POOL_NONE', { name })}</li>;
          if (!p.totalUnits) return <li key={p.key}>{t('UNIT_ECONOMICS.SPLIT.POOL_UNALLOCATED', { name, amount: money(p.monthUsd) })}</li>;
          return <li key={p.key}>{t(`UNIT_ECONOMICS.SPLIT.POOL_${p.key}`, { amount: money(p.monthUsd), units: formatNumber(p.totalUnits) })}</li>;
        })}
        <li>{t('UNIT_ECONOMICS.SPLIT.UNATTRIBUTED', { openrouter: money(un.openrouterUsd), fal: money(un.falUsd) })}</li>
      </ul>
    </SectionCard>
  );
}
